import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';

import { boardsServices, fleetVehiclesServices } from '@services';
import { AGENT_ROLES, GENERIC_ERROR_MESSAGE } from '@constants';
import { showError } from '@utils';

import { QUERY_KEYS } from 'constants/queryKeys';
import { useAssignees } from 'hooks/common';
import { normalizeBuyOutTaskData, normalizeVehicleData } from './utils';

export const useBuyOutTaskOperations = () => {
  const {
    query: { buyoutTaskId, token },
  } = useRouter();
  const {
    assignees: cachedAssignees,
    isLoading: isAssigneesLoading,
    assigneesMap,
  } = useAssignees();

  const {
    data: buyOutTaskData,
    isLoading: isBuyOutTaskDataLoading,
    isError: isBuyOutTasDataError,
    error: buyOutTaskDataError,
    refetch: refetchBuyOutTaskData,
  } = useQuery(
    [QUERY_KEYS.GET_BUY_OUT_TASK_DATA_KEY, buyoutTaskId],
    async () => {
      const task = await boardsServices.getTaskById(buyoutTaskId);

      const carId = task?.data?.customFields?.carId;

      const vehicle = carId
        ? await fleetVehiclesServices.getVehicleByInternalId(carId)
        : {};

      return {
        task: task?.data,
        vehicle: vehicle?.data,
      };
    },
    {
      enabled: !!buyoutTaskId,
      refetchOnMount: false,
    }
  );

  const updateTask = async (id, payload = {}) => {
    try {
      await boardsServices.updateTask(id, payload);
    } catch (error) {
      showError(error);
    }
  };

  const { vehicle, task } = useMemo(() => {
    const buyOutTask = normalizeBuyOutTaskData(buyOutTaskData?.task);
    const buyOutVehicle = normalizeVehicleData(buyOutTaskData?.vehicle);

    return { task: buyOutTask, vehicle: buyOutVehicle };
  }, [buyOutTaskData]);

  const loading = isBuyOutTaskDataLoading || isAssigneesLoading;

  const errorMessage =
    buyOutTaskDataError || isBuyOutTasDataError ? GENERIC_ERROR_MESSAGE : '';

  const loanAssigneesOptions =
    cachedAssignees?.filter(
      (agent) =>
        agent?.role === AGENT_ROLES.isCreditControlLead ||
        agent?.role === AGENT_ROLES.isCreditControlAssociate
    ) || [];

  useEffect(() => {
    if (errorMessage) {
      showError(errorMessage);
    }
  }, [errorMessage]);

  return {
    loading,
    vehicle,
    task,
    taskId: buyoutTaskId,
    errorMessage,
    assignees: cachedAssignees,
    loanAssigneesOptions,
    assigneesMap,
    getBuyOutTask: refetchBuyOutTaskData,
    updateTask,
    token,
  };
};
